import { useEffect, useState } from "react";
import {
    Cloud,
    Network,
    ListTodo,
    Users,
    KeyRound,
    User,
    Blocks,
    LogOut,
    Sun,
    Bell,
    Cpu,
    Server,
    Gauge,
    Pencil,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
} from "recharts";
import "./dashboard.css";

const API_BASE = "/web";

const NAV_ITEMS = [
    { label: "Dashboard", icon: Gauge },
    { label: "Cluster", icon: Network },
    { label: "Tasks", icon: ListTodo },
    { label: "Groups", icon: Users },
    { label: "Permissions", icon: KeyRound },
    { label: "Players", icon: User },
    { label: "Modules", icon: Blocks },
];

// Platzhalter fuer die Zeitreihen-Charts, bis es einen History-Endpoint gibt
const ramData = [
    { time: "04:50", value: 2304.0 },
    { time: "04:55", value: 2304.0 },
    { time: "05:00", value: 2304.0 },
    { time: "05:05", value: 2304.0 },
    { time: "05:10", value: 2304.0 },
    { time: "05:15", value: 2304.0 },
    { time: "05:20", value: 2304.0 },
    { time: "05:25", value: 2304.0 },
    { time: "05:30", value: 2304.0 },
    { time: "05:35", value: 2304.0 },
];

const cpuData = [
    { time: "04:50", value: 38.0 },
    { time: "04:55", value: 38.1 },
    { time: "05:00", value: 38.9 },
    { time: "05:05", value: 38.0 },
    { time: "05:10", value: 38.0 },
    { time: "05:15", value: 38.9 },
    { time: "05:20", value: 38.0 },
    { time: "05:25", value: 38.0 },
    { time: "05:30", value: 38.0 },
    { time: "05:35", value: 38.0 },
];

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="stat-card">
            <div className="stat-icon">
                <Icon size={22} color="var(--accent)" />
            </div>
            <div>
                <div className="stat-label">{label}</div>
                <div className="stat-value">{value}</div>
            </div>
        </div>
    );
}

function ChartPanel({ title, data, color, unitLabel, domain }) {
    return (
        <div className="chart-panel">
            <div className="chart-title">{title}</div>
            <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                    <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid stroke="var(--panel-border)" strokeDasharray="4 4" vertical={false} />
                        <XAxis
                            dataKey="time"
                            stroke="var(--subtext)"
                            tick={{ fill: "var(--subtext)", fontSize: 11 }}
                            tickLine={false}
                        />
                        <YAxis
                            domain={domain}
                            stroke="var(--subtext)"
                            tick={{ fill: "var(--subtext)", fontSize: 11 }}
                            tickLine={false}
                            width={50}
                        />
                        <Tooltip
                            contentStyle={{
                                background: "var(--bg)",
                                border: "1px solid var(--panel-border)",
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            labelStyle={{ color: "var(--subtext)" }}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={color}
                            strokeWidth={2}
                            dot={{ r: 3, fill: color, strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-legend">
                <span className="legend-dot" style={{ background: color }} />
                {unitLabel}
            </div>
        </div>
    );
}

function TerminalPanel() {
    const lines = [
        "Registered: 6482",
        "Highest OnlineCount: 157",
        "Logins: 102372",
        "Command Executions: 345272",
    ];

    return (
        <div className="terminal-panel">
            <div className="terminal-dots">
                <span className="terminal-dot red" />
                <span className="terminal-dot yellow" />
                <span className="terminal-dot green" />
            </div>
            {lines.map((line) => (
                <div key={line} className="terminal-line">
          <span>
            <span className="terminal-prompt">cloudnet:~$</span>{" "}
              <span className="terminal-text">{line}</span>
          </span>
                    <Pencil size={13} className="terminal-edit-icon" />
                </div>
            ))}
        </div>
    );
}

export function Dashboard() {
    const [active, setActive] = useState("Dashboard");
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch(`${API_BASE}/info`)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((data) => setInfo(data))
            .catch((err) => setError(err.message));
    }, []);

    return (
        <div className="dashboard-layout">
            {/* Sidebar */}
            <aside className="sidebar">
                <div>
                    <div className="sidebar-logo">
                        <Cloud size={22} color="var(--accent)" />
                        <span>CloudNet</span>
                    </div>

                    <nav className="sidebar-nav">
                        {NAV_ITEMS.map(({ label, icon: Icon }) => (
                            <button
                                key={label}
                                onClick={() => setActive(label)}
                                className={`nav-item ${active === label ? "active" : ""}`}
                            >
                                <Icon size={16} />
                                {label}
                            </button>
                        ))}
                    </nav>
                </div>

                <button className="logout-btn">
                    <LogOut size={16} />
                    Logout
                </button>
            </aside>

            {/* Main content */}
            <main className="main-content">
                <div className="topbar">
                    <Sun size={18} className="topbar-icon" />
                    <Bell size={18} className="topbar-icon" />
                    <div className="avatar">
                        <User size={16} color="var(--accent)" />
                    </div>
                </div>

                {error && <div className="status-text error">Backend nicht erreichbar: {error}</div>}
                {!info && !error && <div className="status-text">Lade Daten…</div>}

                {/* Stat cards */}
                <div className="stat-row">
                    <StatCard icon={Network} label="Nodes" value={info?.NODES ?? "–"} />
                    <StatCard icon={Server} label="Service count" value={info?.SERVICES ?? "–"} />
                    <StatCard
                        icon={Cpu}
                        label="CPU"
                        value={info ? `${info.CPU_USAGE}/100%` : "–"}
                    />
                    <StatCard
                        icon={Gauge}
                        label="Ram"
                        value={info ? `${info.RAM_USAGE} MB/${info.MAX_RAM}MB` : "–"}
                    />
                </div>

                {/* Charts */}
                <div className="chart-row">
                    <ChartPanel
                        title="Ram Usage"
                        data={ramData}
                        color="#22c55e"
                        unitLabel="Usage in MB"
                        domain={[2303, 2305]}
                    />
                    <ChartPanel
                        title="CPU Usage"
                        data={cpuData}
                        color="#3b82f6"
                        unitLabel="Usage in %"
                        domain={[38, 39]}
                    />
                </div>

                {/* Terminal */}
                <TerminalPanel />
            </main>
        </div>
    );
}

export default Dashboard;